import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Select, MenuItem, InputLabel, FormControl, Container, Chip, Tooltip, Snackbar, Alert } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";

export default function ManageUsers({ onClose }) {
  const [users, setUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [userApiKeys, setUserApiKeys] = useState({});
  const [visibleApiKeys, setVisibleApiKeys] = useState({});
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", username: "", password: "", role: "", account_id: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchInactiveUsers();
    fetchAccounts();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/users/", {
        credentials: "include"
      });
      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);

        usersData.forEach(user => {
          fetchUserApiKey(user.id);
        });
      }
    } catch (error) {
      showSnackbar("Error fetching users", "error");
    }
  };

  const fetchInactiveUsers = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/users/inactive", {
        credentials: "include"
      });
      if (response.ok) {
        const inactiveUsersData = await response.json();
        setInactiveUsers(inactiveUsersData);
      }
    } catch (error) {
      console.error("Error fetching inactive users:", error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/accounts/", {
        credentials: "include"
      });
      if (response.ok) {
        const accountsData = await response.json();
        setAccounts(accountsData);
      }
    } catch (error) {
      showSnackbar("Error fetching accounts", "error");
    }
  };

  const fetchUserApiKey = async (userId) => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/users/${userId}/api-key`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setUserApiKeys(prev => ({
          ...prev,
          [userId]: {
            hasApiKey: data.has_api_key,
            apiKey: data.api_key
          }
        }));
      }
    } catch (error) {
      console.error(`Error fetching API key for user ${userId}:`, error);
    }
  };

  const generateApiKey = async (userId) => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/users/${userId}/generate-api-key`, {
        method: "POST",
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        setUserApiKeys(prev => ({
          ...prev,
          [userId]: {
            hasApiKey: true,
            apiKey: data.api_key
          }
        }));
        showSnackbar("API key generated successfully", "success");
      } else {
        showSnackbar("Error generating API key", "error");
      }
    } catch (error) {
      showSnackbar("Error generating API key", "error");
    }
  };

  const revokeApiKey = async (userId) => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/users/${userId}/api-key`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        setUserApiKeys(prev => ({
          ...prev,
          [userId]: {
            hasApiKey: false,
            apiKey: null
          }
        }));
        setVisibleApiKeys(prev => ({
          ...prev,
          [userId]: false
        }));
        showSnackbar("API key revoked successfully", "success");
      } else {
        showSnackbar("Error revoking API key", "error");
      }
    } catch (error) {
      showSnackbar("Error revoking API key", "error");
    }
  };

  const toggleApiKeyVisibility = (userId) => {
    setVisibleApiKeys(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar("API key copied to clipboard", "success");
    } catch (error) {
      showSnackbar("Failed to copy to clipboard", "error");
    }
  };

  const handleDelete = async (id) => {
    await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/users/${id}`, {
      method: "DELETE",
      credentials: "include"
    });
    setUsers(users.filter(u => u.id !== id));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const createUser = async () => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/users/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        setOpenCreateDialog(false);
        setNewUser({ name: "", email: "", username: "", password: "", role: "", account_id: "" });
        fetchUsers();
        showSnackbar("User created successfully", "success");
      } else {
        const error = await response.json();
        showSnackbar(error.detail || "Error creating user", "error");
      }
    } catch (error) {
      showSnackbar("Error creating user", "error");
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/users/${userId}`, {
          method: "DELETE",
          credentials: "include"
        });

        if (response.ok) {
          fetchUsers();
          showSnackbar("User deleted successfully", "success");
        } else {
          showSnackbar("Error deleting user", "error");
        }
      } catch (error) {
        showSnackbar("Error deleting user", "error");
      }
    }
  };

  const activateUser = async (userId) => {
    try {
      const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/users/${userId}/activate`, {
        method: "PUT",
        credentials: "include"
      });

      if (response.ok) {
        fetchUsers();
        fetchInactiveUsers();
        showSnackbar("User activated successfully", "success");
      } else {
        showSnackbar("Error activating user", "error");
      }
    } catch (error) {
      showSnackbar("Error activating user", "error");
    }
  };

  const deactivateUser = async (userId) => {
    if (window.confirm("Are you sure you want to deactivate this user? They will not be able to log in.")) {
      try {
        const response = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/users/${userId}/deactivate`, {
          method: "PUT",
          credentials: "include"
        });

        if (response.ok) {
          fetchUsers();
          fetchInactiveUsers();
          showSnackbar("User deactivated successfully", "success");
        } else {
          showSnackbar("Error deactivating user", "error");
        }
      } catch (error) {
        showSnackbar("Error deactivating user", "error");
      }
    }
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const maskApiKey = (apiKey) => {
    if (!apiKey) return "";
    return apiKey.substring(0, 8) + "..." + apiKey.substring(apiKey.length - 4);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Inactive Users Section */}
      {inactiveUsers.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#fff3e0' }}>
          <Typography variant="h5" component="h2" gutterBottom color="warning.main">
            Pending User Activations ({inactiveUsers.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These users have signed up and are awaiting activation
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Account</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inactiveUsers.map(user => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell sx={{ fontSize: '0.875rem' }}>{user.email}</TableCell>
                  <TableCell>
                    {user.account_name ? (
                      <Chip label={user.account_name} size="small" color="primary" variant="outlined" />
                    ) : (
                      <Chip label="No Account" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => activateUser(user.id)}
                    >
                      Activate
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      
      {/* Active Users Section */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Manage Users
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
          >
            Create User
          </Button>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Username</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Account</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>API Key</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => {
              const userApiKeyData = userApiKeys[user.id] || { hasApiKey: false, apiKey: null };
              const isKeyVisible = visibleApiKeys[user.id];

              return (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell sx={{ fontSize: '0.875rem' }}>{user.username}</TableCell>
                  <TableCell sx={{ fontSize: '0.875rem' }}>{user.email}</TableCell>
                  <TableCell>
                    {user.account_name ? (
                      <Chip label={user.account_name} size="small" color="primary" variant="outlined" />
                    ) : (
                      <Chip label="No Account" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {user.active === 1 ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Active"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        icon={<BlockIcon />}
                        label="Inactive"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {userApiKeyData.hasApiKey ? (
                        <>
                          <Chip
                            icon={<KeyIcon fontSize="small" />}
                            label={
                              isKeyVisible
                                ? userApiKeyData.apiKey
                                : maskApiKey(userApiKeyData.apiKey)
                            }
                            variant="outlined"
                            size="small"
                            clickable={false}
                            sx={{ fontFamily: "monospace", maxWidth: 150, fontSize: '0.75rem' }}
                          />
                          <Tooltip title={isKeyVisible ? "Hide" : "Show"}>
                            <IconButton
                              size="small"
                              onClick={() => toggleApiKeyVisibility(user.id)}
                              sx={{ p: 0.5 }}
                            >
                              {isKeyVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          {isKeyVisible && (
                            <Tooltip title="Copy">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(userApiKeyData.apiKey)}
                                sx={{ p: 0.5 }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            onClick={() => revokeApiKey(user.id)}
                            sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                          >
                            Revoke
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<KeyIcon fontSize="small" />}
                          onClick={() => generateApiKey(user.id)}
                          sx={{ fontSize: '0.75rem', py: 0.5 }}
                        >
                          Generate
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} alignItems="center">
                      {user.active === 1 ? (
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          startIcon={<BlockIcon fontSize="small" />}
                          onClick={() => deactivateUser(user.id)}
                          sx={{ fontSize: '0.75rem', py: 0.5 }}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          color="success"
                          startIcon={<CheckCircleIcon fontSize="small" />}
                          onClick={() => activateUser(user.id)}
                          sx={{ fontSize: '0.75rem', py: 0.5 }}
                        >
                          Activate
                        </Button>
                      )}
                      <Tooltip title="Delete user">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => deleteUser(user.id)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      
      {/* Create User Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)}>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Full Name"
            fullWidth
            variant="outlined"
            value={newUser.name}
            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Username"
            fullWidth
            variant="outlined"
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Account"
            select
            fullWidth
            variant="outlined"
            value={newUser.account_id}
            onChange={(e) => setNewUser({ ...newUser, account_id: e.target.value })}
          >
            <MenuItem value="">
              <em>No Account</em>
            </MenuItem>
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Role"
            select
            fullWidth
            variant="outlined"
            value={newUser.role}
            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
          >
            <MenuItem value="User">User</MenuItem>
            <MenuItem value="Council">Council</MenuItem>
            <MenuItem value="Architect">Architect</MenuItem>
            <MenuItem value="Superadmin">Superadmin</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button onClick={createUser} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}